import {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {Col, Row, Table, Tag, Popconfirm, notification} from "antd";
import Button from "~/components/Button";
import Loading from "~/components/Loading";
import {Icon, StatusLabel} from "~/components";
import {DateField, SelectField, InputField, TextAreaField} from "~/components/Forms";
import {probationHistoryApi} from "~/api";
import {apiError, handleRequest, strToTime, renderDate} from "~/utils";
import {useCan} from "~/hooks";

const roleOptions = [
	{value: 'internship', label: 'Thực tập sinh'},
	{value: 'probation', label: 'Thử việc'},
	{value: 'official', label: 'Chính thức'},
];

const initialValues = {
	role: 'internship',
	startTime: undefined,
	endTime: undefined,
	salaryPercent: 100,
	note: undefined,
};

const validationSchema = Yup.object().shape({
	role: Yup.string().required('Bạn chưa chọn vai trò'),
	startTime: Yup.string().required('Ngày bắt đầu không được để trống'),
	endTime: Yup.mixed().when('role', {
		is: (role) => role === 'internship' || role === 'probation',
		then: Yup.mixed().test(
			'end-required',
			'Vai trò Thực tập sinh / Thử việc bắt buộc phải chọn ngày kết thúc',
			(value) => value !== undefined && value !== null && value !== ''
		),
	}),
	salaryPercent: Yup.number()
		.typeError('Phần trăm lương phải là số')
		.min(0, 'Phần trăm lương tối thiểu là 0')
		.max(100, 'Phần trăm lương tối đa là 100')
		.required('Phần trăm lương không được để trống'),
});

function PersonnelProbationHistory({item}) {

	const can = {
		add: useCan('probationHistoryAdd'),
		edit: useCan('probationHistoryEdit'),
		delete: useCan('probationHistoryDelete'),
	};

	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [editing, setEditing] = useState(null);

	const {control, handleSubmit, formState: {isSubmitting, errors}, reset} = useForm({
		defaultValues: initialValues,
		resolver: yupResolver(validationSchema),
	});

	//Giá trị mặc định cho form thêm mới: gợi ý ngày bắt đầu = ngày liền sau giai đoạn cuối (để liền mạch)
	const computeAddDefaults = (list) => {
		const ends = (list || []).map((it) => it.endTime).filter(Boolean);
		let startTime = undefined;
		if (ends.length > 0) {
			const maxEnd = Math.max(...ends);
			startTime = (maxEnd + 1) * 1000; // ms - ngày liền sau ngày kết thúc gần nhất
		}
		return {...initialValues, startTime};
	};

	//Load danh sách giai đoạn của nhân viên
	const loadData = async () => {
		if (!item?.id) return;
		setLoading(true);
		let [error, response] = await handleRequest(probationHistoryApi.gets(item.id));
		setLoading(false);
		let message = apiError('Tải lịch sử thực tập - thử việc thất bại', error, response);
		if (!message) {
			const list = response.data || [];
			setItems(list);
			setEditing(null);
			reset(computeAddDefaults(list));
		} else {
			notification.error({message: 'Lỗi', description: message});
		}
	};

	useEffect(() => {
		loadData();
	}, [item?.id]);

	//Chuyển sang chế độ sửa 1 giai đoạn
	const handleEdit = (record) => {
		setEditing(record);
		reset({
			role: record.role.key,
			startTime: record.startTime * 1000,
			endTime: record.endTime ? record.endTime * 1000 : undefined,
			salaryPercent: record.salaryPercent,
			note: record.note || undefined,
		});
	};

	const handleCancelEdit = () => {
		setEditing(null);
		reset(computeAddDefaults(items));
	};

	const handleDelete = async (record) => {
		let [error, response] = await handleRequest(probationHistoryApi.delete(record.id));
		let message = apiError('Xóa giai đoạn thất bại', error, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Xóa giai đoạn thành công'});
			const newList = items.filter((it) => it.id !== record.id);
			setItems(newList);
			setEditing(null);
			reset(computeAddDefaults(newList));
		} else {
			notification.error({message: 'Lỗi', description: message});
		}
	};

	const onSubmit = async (data) => {
		const payload = {
			userId: item.id,
			role: data.role,
			startTime: strToTime(data.startTime),
			endTime: data.endTime ? strToTime(data.endTime) : '',
			salaryPercent: Number(data.salaryPercent),
			note: data.note || '',
		};

		let error, response;

		if (editing?.id) {
			payload.id = editing.id;
			[error, response] = await handleRequest(probationHistoryApi.update(payload));
		} else {
			[error, response] = await handleRequest(probationHistoryApi.add(payload));
		}

		let message = apiError('Lưu giai đoạn thất bại', error, response);

		if (!message) {
			notification.success({message: 'Thành công', description: editing?.id ? 'Cập nhật giai đoạn thành công' : 'Thêm giai đoạn thành công'});
			const newList = editing?.id
				? items.map((it) => (it.id === response.data.id ? response.data : it))
				: [...items, response.data];
			setItems(newList);
			setEditing(null);
			reset(computeAddDefaults(newList));
		} else {
			notification.error({message: 'Lỗi', description: message});
		}
	};

	const columns = [
		{
			title: 'Vai trò',
			dataIndex: 'role',
			render: (role) => <StatusLabel small type={role.color}>{role.label}</StatusLabel>,
		},
		{
			title: 'Bắt đầu',
			dataIndex: 'startTime',
			render: (time) => renderDate(time),
		},
		{
			title: 'Kết thúc',
			dataIndex: 'endTime',
			render: (time) => (time ? renderDate(time) : <Tag color="processing">Hiện tại</Tag>),
		},
		{
			title: '% lương',
			dataIndex: 'salaryPercent',
			align: 'center',
			render: (val) => `${val}%`,
		},
		{
			title: 'Ghi chú',
			dataIndex: 'note',
			render: (note) => note || '-',
		},
		{
			title: '',
			key: 'action',
			align: 'right',
			render: (_, record) => (
				<div className="d-flex justify-content-end gap">
					{can.edit && <Button background blue leftIcon={Icon.edit} onClick={() => handleEdit(record)}/>}
					{can.delete && (
						<Popconfirm title="Xóa giai đoạn" description="Bạn có chắc chắn muốn xóa giai đoạn này?" onConfirm={() => handleDelete(record)}>
							<Button background red noneBorder leftIcon={Icon.delete}/>
						</Popconfirm>
					)}
				</div>
			),
		},
	];

	return (
		<div className="form">
			{loading && <Loading/>}

			<p className="heading">Lịch sử thực tập - thử việc</p>
			<Table
				rowKey="id"
				size="small"
				columns={columns}
				dataSource={items}
				pagination={false}
				locale={{emptyText: 'Chưa có giai đoạn nào'}}
			/>

			{(can.add || can.edit) && (
				<form onSubmit={handleSubmit(onSubmit)} style={{marginTop: 16}}>
					{isSubmitting && <Loading/>}
					<p className="heading">{editing?.id ? 'Cập nhật giai đoạn' : 'Thêm giai đoạn mới'}</p>
					<Row gutter={10}>
						<Col className="gutter-row" span={12}>
							<Controller control={control} name="role" render={({field}) => (
								<SelectField label="Vai trò" options={roleOptions} errors={errors} {...field}/>
							)}/>
						</Col>
						<Col className="gutter-row" span={12}>
							<Controller control={control} name="salaryPercent" render={({field}) => (
								<InputField type="number" label="Phần trăm lương (%)" errors={errors} {...field}/>
							)}/>
						</Col>
					</Row>
					<Row gutter={10}>
						<Col className="gutter-row" span={12}>
							<Controller control={control} name="startTime" render={({field}) => (
								<DateField label="Ngày bắt đầu" errors={errors} {...field}/>
							)}/>
						</Col>
						<Col className="gutter-row" span={12}>
							<Controller control={control} name="endTime" render={({field}) => (
								<DateField label="Ngày kết thúc (để trống nếu chưa kết thúc)" errors={errors} {...field}/>
							)}/>
						</Col>
					</Row>
					<Controller control={control} name="note" render={({field}) => (
						<TextAreaField label="Ghi chú" errors={errors} {...field}/>
					)}/>
					<hr/>
					<div className="d-flex justify-content-end gap">
						{editing?.id && <Button white leftIcon={Icon.close} type="button" onClick={handleCancelEdit}>Hủy</Button>}
						<Button primary type="submit" leftIcon={Icon.save}>{editing?.id ? 'Cập nhật' : 'Thêm giai đoạn'}</Button>
					</div>
				</form>
			)}
		</div>
	);
}

export default PersonnelProbationHistory;
